import React, { useState, useEffect, useMemo } from 'react';
import { getOfficeConfig } from '../config/offices';
import VirtualKeyboard from './VirtualKeyboard';

// Generate time slots in 30-min increments from 7 AM to 7 PM
function generateTimeSlots() {
  const slots = [];
  for (let h = 7; h < 19; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const min = m.toString().padStart(2, '0');
      const label = `${h > 12 ? h - 12 : h}:${min} ${h >= 12 ? 'PM' : 'AM'}`;
      slots.push({ value: `${hour}:${min}`, label });
    }
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

// Duration options in minutes
const DURATIONS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
  { value: 720, label: 'Full day' },
];

export default function BookingPanel({
  selectedRoomId,
  availability = {},
  onBook,
  onCancel,
  isKiosk = false,
  existingBooking = null, // If user already has an office/cubicle booked
  user = null,
  directBooking = null, // Pre-filled from QR deep link
  selectedDate = null, // Date from the view's date picker
}) {
  const [date, setDate] = useState(() => directBooking?.date || selectedDate || new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState(() => {
    if (directBooking?.startTime) return directBooking.startTime;
    // Default to next full hour (or 7 AM if before 7 AM)
    const now = new Date();
    let nextHour = now.getHours() + 1;
    if (nextHour < 7) nextHour = 7;
    if (nextHour > 18) nextHour = 18;
    return `${nextHour.toString().padStart(2, '0')}:00`;
  });
  const [duration, setDuration] = useState(() => {
    if (directBooking?.startTime && directBooking?.endTime) {
      const [sh, sm] = directBooking.startTime.split(':').map(Number);
      const [eh, em] = directBooking.endTime.split(':').map(Number);
      return (eh * 60 + em) - (sh * 60 + sm);
    }
    return 60;
  });
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [subject, setSubject] = useState(directBooking?.subject || '');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringDays, setRecurringDays] = useState([]);
  const [recurringEndDate, setRecurringEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 28);
    return d.toISOString().split('T')[0];
  });
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [showKeyboard, setShowKeyboard] = useState(false);

  const isAnyAvailable = selectedRoomId === 'any-cubicle' || selectedRoomId === 'any-office';
  const config = getOfficeConfig();

  const room = useMemo(() => {
    if (isAnyAvailable) {
      const type = selectedRoomId === 'any-cubicle' ? 'cubicle' : 'office';
      return {
        id: selectedRoomId,
        name: selectedRoomId === 'any-cubicle' ? 'Any Available Cubicle' : 'Any Available Office',
        type,
        capacity: 1,
      };
    }
    return config.rooms.find(r => r.id === selectedRoomId);
  }, [selectedRoomId, isAnyAvailable, config.rooms]);

  // Advance booking limits per room type:
  // Conference/meeting rooms: no limit. Everything else: 4 weeks.
  const maxDate = useMemo(() => {
    if (!room) return undefined;
    if (room.type === 'conference' || room.type === 'meeting') return undefined;
    const max = new Date();
    max.setDate(max.getDate() + 28); // 4 weeks
    return max.toISOString().split('T')[0];
  }, [room]);

  // Default offices and cubicles to full day (7 AM - 7 PM)
  useEffect(() => {
    if (room && !defaultsApplied && !directBooking) {
      if (room.type === 'office' || room.type === 'cubicle') {
        setStartTime('07:00');
        setDuration(720); // 12 hours
      }
      setDefaultsApplied(true);
    }
  }, [room, defaultsApplied, directBooking]);

  // Calculate end time from start + duration
  const endTime = useMemo(() => {
    const [h, m] = startTime.split(':').map(Number);
    const totalMin = h * 60 + m + duration;
    const endH = Math.floor(totalMin / 60).toString().padStart(2, '0');
    const endM = (totalMin % 60).toString().padStart(2, '0');
    return `${endH}:${endM}`;
  }, [startTime, duration]);

  const endTimeLabel = useMemo(() => {
    const [h, m] = endTime.split(':').map(Number);
    return `${h > 12 ? h - 12 : h}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  }, [endTime]);

  // Existing bookings for this room on the selected date
  const existingSlots = useMemo(() => {
    return availability[selectedRoomId]?.slots || [];
  }, [availability, selectedRoomId]);

  // Check if the selected time range overlaps any existing booking
  const timeConflict = useMemo(() => {
    if (!room || !(room.type === 'conference' || room.type === 'meeting' || room.type === 'huddle')) return null;
    const [sh, sm] = startTime.split(':').map(Number);
    const newStartMin = sh * 60 + sm;
    const newEndMin = newStartMin + duration;

    for (const slot of existingSlots) {
      // Parse slot times (may be ISO datetime or HH:MM)
      let slotStartMin, slotEndMin;
      if (slot.start.includes('T')) {
        const sd = new Date(slot.start.endsWith('Z') ? slot.start : slot.start + 'Z');
        const central = new Date(sd.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
        slotStartMin = central.getHours() * 60 + central.getMinutes();
        const ed = new Date(slot.end.endsWith('Z') ? slot.end : slot.end + 'Z');
        const centralEnd = new Date(ed.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
        slotEndMin = centralEnd.getHours() * 60 + centralEnd.getMinutes();
      } else {
        const [ssH, ssM] = slot.start.split(':').map(Number);
        slotStartMin = ssH * 60 + ssM;
        const [seH, seM] = slot.end.split(':').map(Number);
        slotEndMin = seH * 60 + seM;
      }

      // Overlap check: new booking starts before existing ends AND new booking ends after existing starts
      if (newStartMin < slotEndMin && newEndMin > slotStartMin) {
        return slot;
      }
    }
    return null;
  }, [room, startTime, duration, existingSlots]);

  // Warn if user already has a booking for offices/cubicles
  const oneBookingWarning = useMemo(() => {
    if (!existingBooking) return null;
    if (room?.type === 'office' || room?.type === 'cubicle') {
      return `You already have ${existingBooking.roomName} booked for today. Booking this space will release your current reservation.`;
    }
    return null;
  }, [existingBooking, room]);

  if (!room) return null;

  const handleBook = () => {
    setError(null);
    if (isRecurring && recurringDays.length === 0) {
      setError('Select at least one day for your recurring booking.');
      return;
    }
    if (timeConflict) {
      setError('This time overlaps an existing booking. Choose a different time.');
      return;
    }
    setConfirming(true);
  };

  const handleConfirm = () => {
    // Default subject depends on room type:
    // Offices/cubicles: "Employee Name - Room Name"
    // Conference/meeting/huddle: "First Name's Meeting"
    let defaultSubject;
    if (room.type === 'office' || room.type === 'cubicle') {
      defaultSubject = user?.name ? `${user.name} - ${room.name}` : room.name;
    } else {
      defaultSubject = user?.name ? `${user.name.split(' ')[0]}'s Meeting` : 'Meeting';
    }

    const booking = {
      roomId: selectedRoomId,
      roomName: room.name,
      roomType: room.type,
      date,
      startTime,
      endTime,
      duration,
      subject: subject.trim() || defaultSubject,
    };

    if (isRecurring && recurringDays.length > 0) {
      booking.recurrence = {
        daysOfWeek: recurringDays,
        startDate: date,
        endDate: recurringEndDate,
      };
    }

    onBook?.(booking);
    setConfirming(false);
  };

  const typeIcon = {
    conference: '🏢',
    meeting: '📋',
    huddle: '💬',
    office: '🖥️',
    cubicle: '💻',
  };

  return (
    <div className={`booking-panel ${isKiosk ? 'kiosk' : ''}`}>
      <div className="booking-panel-header">
        <h3>{typeIcon[room.type]} {room.name}</h3>
        {room.capacity && <span className="capacity">Capacity: {room.capacity}</span>}
        <button className="close-btn" onClick={onCancel} aria-label="Close">x</button>
      </div>

      {oneBookingWarning && (
        <div className="booking-warning">
          {oneBookingWarning}
        </div>
      )}

      {error && <div className="booking-error">{error}</div>}

      {!confirming ? (
        <div className="booking-form">
          <div className="form-group">
            <label htmlFor="booking-date">Date</label>
            <input
              id="booking-date"
              type="date"
              value={date}
              min={new Date().toISOString().split('T')[0]}
              max={maxDate}
              onChange={(e) => setDate(e.target.value)}
            />
            {maxDate && (
              <span className="form-hint">Up to 4 weeks in advance</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="booking-start">Start Time</label>
              <select
                id="booking-start"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              >
                {TIME_SLOTS.map(slot => (
                  <option key={slot.value} value={slot.value}>{slot.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="booking-duration">Duration</label>
              <select
                id="booking-duration"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                {DURATIONS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="time-summary">
            {TIME_SLOTS.find(s => s.value === startTime)?.label} - {endTimeLabel}
          </div>

          {room.type === 'cubicle' && (
            <div className="recurring-section">
              <label className="recurring-toggle">
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                />
                Recurring booking
              </label>

              {isRecurring && (
                <>
                  <div className="recurring-days">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map(day => (
                      <label key={day} className={`day-chip ${recurringDays.includes(day) ? 'day-chip--selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={recurringDays.includes(day)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRecurringDays(prev => [...prev, day]);
                            } else {
                              setRecurringDays(prev => prev.filter(d => d !== day));
                            }
                          }}
                        />
                        {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                      </label>
                    ))}
                  </div>
                  <div className="form-group">
                    <label htmlFor="recurring-end">Repeat until</label>
                    <input
                      id="recurring-end"
                      type="date"
                      value={recurringEndDate}
                      min={date}
                      max={maxDate}
                      onChange={(e) => setRecurringEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {(room.type === 'conference' || room.type === 'meeting' || room.type === 'huddle') && (
            <div className="form-group">
              <label htmlFor="booking-subject">Meeting Subject</label>
              {isKiosk ? (
                <div
                  className="kiosk-subject-input"
                  onClick={() => setShowKeyboard(true)}
                >
                  {subject || <span className="placeholder">Tap to enter meeting subject</span>}
                </div>
              ) : (
                <input
                  id="booking-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Sprint Planning, 1:1 with Casey"
                  maxLength={100}
                />
              )}
            </div>
          )}

          {/* Show existing bookings for shared rooms so users can pick an open slot */}
          {(room.type === 'conference' || room.type === 'meeting' || room.type === 'huddle') && existingSlots.length > 0 && (
            <div className="existing-bookings">
              <label>Existing bookings today:</label>
              <div className="existing-bookings-list">
                {existingSlots.map((slot, i) => (
                  <div key={i} className="existing-booking-slot">
                    <span className="existing-slot-time">{formatSlotTime(slot.start)} - {formatSlotTime(slot.end)}</span>
                    <span className="existing-slot-who">{slot.organizer || 'Reserved'}</span>
                  </div>
                ))}
              </div>
              {timeConflict && (
                <div className="booking-conflict-warning">
                  Your selected time overlaps with an existing booking.
                </div>
              )}
            </div>
          )}

          <button className="book-btn" onClick={handleBook} disabled={!!timeConflict}>
            {isKiosk ? 'Generate QR Code to Book' : 'Book This Space'}
          </button>
        </div>
      ) : (
        <div className="booking-confirm">
          <h4>Confirm Booking</h4>
          <div className="confirm-details">
            <p><strong>Space:</strong> {room.name}</p>
            <p><strong>Date:</strong> {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
            <p><strong>Time:</strong> {TIME_SLOTS.find(s => s.value === startTime)?.label} - {endTimeLabel}</p>
            {isRecurring && recurringDays.length > 0 && (
              <p><strong>Repeats:</strong> Every {recurringDays.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ')} until {new Date(recurringEndDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
            )}
            {subject && <p><strong>Subject:</strong> {subject}</p>}
          </div>
          <div className="confirm-actions">
            <button className="confirm-btn" onClick={handleConfirm}>Confirm</button>
            <button className="cancel-btn" onClick={() => setConfirming(false)}>Go Back</button>
          </div>
        </div>
      )}

      {isKiosk && showKeyboard && (
        <VirtualKeyboard
          value={subject}
          onChange={(val) => setSubject(val.slice(0, 100))}
          onClose={() => setShowKeyboard(false)}
        />
      )}
    </div>
  );
}

function formatSlotTime(timeStr) {
  if (!timeStr) return '';
  let h, m;
  if (timeStr.includes('T')) {
    const d = new Date(timeStr.endsWith('Z') ? timeStr : timeStr + 'Z');
    const central = new Date(d.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    h = central.getHours();
    m = central.getMinutes();
  } else {
    [h, m] = timeStr.split(':').map(Number);
  }
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${m.toString().padStart(2, '0')} ${suffix}`;
}
